/**
 * Mooncloak GameDay Mockup
 * Simulates how an ad appears in Mooncloak GameDay section
 */

import React from 'react';
import { Shield, Trophy, MapPin, Clock } from 'lucide-react';

interface MooncloakGameDayMockupProps {
  children: React.ReactNode;
  adWidth: number;
  adHeight: number;
}

export function MooncloakGameDayMockup({ children, adWidth, adHeight }: MooncloakGameDayMockupProps) {
  const isHero = adWidth > 800;
  const isSidebar = adHeight > adWidth * 1.5;

  return (
    <div className="bg-[#0a0e17] rounded-xl overflow-hidden shadow-2xl w-full max-w-[700px]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1a472a] to-[#2d5a3d] px-4 py-3 flex items-center gap-3">
        <Shield className="w-5 h-5 text-[#FFE17D]" />
        <span className="text-white font-semibold text-sm">mooncloak</span>
        <div className="ml-auto flex items-center gap-2">
          <Trophy className="w-4 h-4 text-[#FFE17D]" />
          <span className="text-white/90 text-sm font-medium">GameDay</span>
        </div>
      </div>

      {/* Game info bar */}
      <div className="bg-[#111428] px-4 py-2 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-blue-600" />
            <span className="text-white text-sm font-medium">NYG</span>
          </div>
          <span className="text-white/50 text-xs">vs</span>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-green-600" />
            <span className="text-white text-sm font-medium">PHI</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-white/60 text-xs">
          <div className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            <span>Today, 1:00 PM</span>
          </div>
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span>MetLife Stadium</span>
          </div>
        </div>
      </div>

      {/* Content area */}
      <div className="p-4">
        {isHero ? (
          <>
            {/* Hero ad at top */}
            <div className="rounded-lg overflow-hidden mb-4">
              {children}
            </div>
            <div className="text-[10px] text-white/30 -mt-3 mb-4">Sponsored</div>
          </>
        ) : isSidebar ? (
          <div className="flex gap-4">
            {/* Main content */}
            <div className="flex-1 space-y-3">
              <h3 className="text-white font-semibold">Nearby Bars & Restaurants</h3>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-white/5">
                  <div className="w-12 h-12 rounded bg-white/10 flex-shrink-0" />
                  <div className="flex-1 space-y-1">
                    <div className="h-3 bg-white/20 rounded w-3/4" />
                    <div className="h-2 bg-white/10 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
            {/* Sidebar ad */}
            <div className="w-[200px] flex-shrink-0">
              <div className="rounded-lg overflow-hidden">
                {children}
              </div>
              <div className="text-[10px] text-white/30 mt-1">Sponsored</div>
            </div>
          </div>
        ) : (
          <>
            {/* Native card ad */}
            <h3 className="text-white font-semibold mb-3">Featured</h3>
            <div className="rounded-lg overflow-hidden">
              {children}
            </div>
            <div className="text-[10px] text-white/30 mt-1">Sponsored</div>

            {/* More content below */}
            <h3 className="text-white font-semibold mt-4 mb-3">Food & Drinks</h3>
            <div className="grid grid-cols-2 gap-3">
              {[1, 2].map((i) => (
                <div key={i} className="p-3 rounded-lg bg-white/5">
                  <div className="w-full aspect-video rounded bg-white/10 mb-2" />
                  <div className="h-3 bg-white/20 rounded w-3/4" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
